// HeroSection.jsx
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import EmailJoinForm from "./NewsletterSection/EmailJoinForm";
import NewsletterSlider from "./NewsletterSection/NewsletterSlider";
import RoleSelection from "./NewsletterSection/RoleSelection";
import ThankYouMessage from "./NewsletterSection/ThankYouMessage";
import useHeroStore from "./NewsletterSection/NewsletterLogic/useHeroStore";
import ProgressBar from "./NewsletterSection/ProgressBar";
import { useAuth } from '../../../contexts/AuthContext';

const roles = [
  { name: "Lawyer", emoji: "⚖️🧑‍⚖️🏛️" },
  { name: "Accountant", emoji: "💰📊🧾" },
  { name: "IT Practitioner", emoji: "💻🖥️🧑‍💻" },
  { name: "Fellow Entrepreneur", emoji: "🚀💡🤝" },
];

const HeroSection = ({ onShowAlert }) => {
  const { user } = useAuth();
  const {
    name,
    setName,
    email,
    setEmail,
    contact,
    setContact,
    address,
    setAddress,
    isLoading,
    setIsLoading,
    isJoinClicked,
    setIsJoinClicked,
    newsletterConfirmed,
    setNewsletterConfirmed,
    sliderValue,
    setSliderValue,
    selectedRole,
    setSelectedRole,
    finalSubscribeChecked,
    setFinalSubscribeChecked,
    proceedToThankYou,
    setProceedToThankYou,
    sendDetailsToTelegram,
  } = useHeroStore();

  // Track when to send Telegram message => useEffect to sync side effect after states updated
  useEffect(() => {
    const shouldSend =
      proceedToThankYou &&
      ((newsletterConfirmed === "skipped" && (finalSubscribeChecked || finalSubscribeChecked === false)) ||
        newsletterConfirmed === "subscribed");

    if (shouldSend) {
      (async () => {
        try {
          await sendDetailsToTelegram();
        } catch (e) {
          // Optional: handle errors globally
        }
      })();
    }
  }, [proceedToThankYou, newsletterConfirmed, finalSubscribeChecked, sendDetailsToTelegram]);

  const handleJoinClick = async () => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // Send details immediately after join
      await sendDetailsToTelegram();

      // Save to localStorage for Business Control Room access
      const signupData = {
        name,
        email,
        contact,
        address,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('busin.us-signup', JSON.stringify(signupData));

      // Move to next step after sending Telegram message
      setIsJoinClicked(true);
      setNewsletterConfirmed(null);
      setSliderValue(50);
      setSelectedRole(null);
      setProceedToThankYou(false);
      setFinalSubscribeChecked(false);
    } catch (error) {
      console.error("Join failed", error);
      // Optionally handle error UI here
    }
    setIsLoading(false);
  };

  const handleSliderRelease = () => {
    if (sliderValue <= 30) {
      setNewsletterConfirmed("skipped");
    } else if (sliderValue >= 70) {
      setNewsletterConfirmed("subscribed");
    } else {
      setSliderValue(50);
      onShowAlert && onShowAlert("Please move the slider left or right to subscribe or skip the newsletter.");
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setProceedToThankYou(true);
  };

  const handleSkipRoleSelection = () => {
    setSelectedRole("Skipped");
    setProceedToThankYou(true);
  };

  // The final newsletter subscription confirmation is triggered by a button click in RoleSelection,
  // not immediately on checkbox toggle — so here just update finalSubscribeChecked state.
  const handleFinalSubscribeCheckChange = (checked) => {
    setFinalSubscribeChecked(checked);
  };

  const handleFinalConfirm = () => {
    if (newsletterConfirmed === "skipped" && !finalSubscribeChecked) {
      // User skipped newsletter and did not opt in now — proceed showing thank you and send data
      setProceedToThankYou(true);
    } else if (finalSubscribeChecked) {
      // User confirmed subscription via checkbox
      setNewsletterConfirmed("subscribed");
      setProceedToThankYou(true);
    } else if (newsletterConfirmed === "subscribed") {
      // Already subscribed, proceed
      setProceedToThankYou(true);
    } else {
      // Should not happen practically
      onShowAlert && onShowAlert("Please confirm your newsletter subscription status.");
    }
  };

  // Calculate current step for ProgressBar
  let currentStep = 1;
  if (!isJoinClicked && !selectedRole) currentStep = 1;
  else if (isJoinClicked && newsletterConfirmed === null) currentStep = 2;
  else if (
    (newsletterConfirmed === "subscribed" || newsletterConfirmed === "skipped") &&
    !selectedRole
  ) {
    currentStep = 3;
  } else if (selectedRole && proceedToThankYou) {
    currentStep = 4;
  }

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center pt-20 sm:pt-24 lg:pt-16 bg-slate-900 text-white px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Left pane */}
        <div className="text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Workflow AI Announcement */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 border-l-4 border-yellow-400 p-3 sm:p-4 mb-6 rounded-r-lg shadow-md">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-white font-bold text-xs sm:text-sm lg:text-base leading-tight">
                    <span className="inline-block mr-2 text-base">🚀</span>
                    <span className="hidden sm:inline">Introducing our Business Control Room - </span>
                    <span className="sm:hidden">Business Control Room - </span>
                    Automate your business processes today!
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href="/business-control-room"
                    className={`inline-block px-3 py-2 sm:px-4 font-bold text-xs sm:text-sm rounded-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap ${
                      user 
                        ? 'bg-white text-emerald-700 hover:bg-yellow-100'
                        : 'bg-white/90 text-emerald-700 hover:bg-white border-2 border-yellow-400'
                    }`}
                  >
                    {user ? 'Launch Control Room →' : 'Try Now (Login Required) →'}
                  </a>
                </div>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight">
              Launch Your Business{" "}
              <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                Globally
              </span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed max-w-2xl lg:max-w-none">
              Transform your vision into a global empire with our comprehensive
              business assistance, legal compliance, and strategic growth solutions.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="max-w-lg mx-auto lg:mx-0 w-full"
          >
            <div className="relative bg-slate-800/60 backdrop-blur-md rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-700/50 shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-600/10 rounded-xl sm:rounded-2xl"></div>
              <div className="relative z-10">
              <ProgressBar currentStep={currentStep} />

              {/* Step 1: Join Form */}
              {!isJoinClicked && !selectedRole && (
                <EmailJoinForm
                  name={name}
                  setName={setName}
                  email={email}
                  setEmail={setEmail}
                  isLoading={isLoading}
                  onJoin={handleJoinClick}
                />
              )}

              {/* Step 2: Newsletter Slider */}
              {isJoinClicked && newsletterConfirmed === null && (
                <NewsletterSlider
                  sliderValue={sliderValue}
                  setSliderValue={setSliderValue}
                  onRelease={handleSliderRelease}
                />
              )}

              {/* Step 3: Role Selection */}
              {(newsletterConfirmed === "subscribed" || newsletterConfirmed === "skipped") &&
                !selectedRole && (
                  <RoleSelection
                    newsletterConfirmed={newsletterConfirmed}
                    finalSubscribeChecked={finalSubscribeChecked}
                    setFinalSubscribeChecked={handleFinalSubscribeCheckChange}
                    onConfirm={handleFinalConfirm}
                    roles={roles}
                    onRoleSelect={handleRoleSelect}
                    onSkipRole={handleSkipRoleSelection}
                  />
                )}

              {/* Step 4: Thank You */}
              {selectedRole && proceedToThankYou && (
                <ThankYouMessage
                  name={name}
                  role={selectedRole}
                  newsletterConfirmed={newsletterConfirmed}
                  finalSubscribeChecked={finalSubscribeChecked}
                />
              )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right pane - image */}
        <div className="relative mt-8 lg:mt-0 order-first lg:order-last">
          <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl sm:rounded-3xl blur-2xl opacity-20"></div>
          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1542641728-6ca359b085f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHRlYW18ZW58MHx8fGJsdWV8MTc1MjUyMjE0OHww&ixlib=rb-4.1.0&q=85"
              alt="Global Business Success"
              className="rounded-2xl sm:rounded-3xl shadow-2xl w-full h-auto max-h-[400px] sm:max-h-[500px] lg:max-h-none object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent rounded-2xl sm:rounded-3xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;